import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const serviceImages = [
  'https://images.unsplash.com/photo-1682888813789-c39fe30921e2?w=800&q=80',
  'https://images.unsplash.com/photo-1765766600820-58eaf8687f1d?w=800&q=80',
  'https://images.unsplash.com/photo-1760072513367-55182245e76c?w=800&q=80',
];

const serviceDescriptions = {
  en: [
    'Design and execution of kitchens with premium materials, architectural finishes, and exceptional functionality.',
    'Transformation of spaces into relaxation sanctuaries with marble, ambient lighting, and boutique hotel design.',
    'Complete management of residential projects with multi-disciplinary coordination and continuous supervision.',
  ],
  es: [
    'Diseño y ejecución de cocinas con materiales premium, acabados arquitectónicos y funcionalidad excepcional.',
    'Transformación de espacios en santuarios de relajación con mármol, iluminación ambiental y diseño de hotel boutique.',
    'Gestión completa de proyectos residenciales con coordinación multi-disciplinaria y supervisión continua.',
  ],
};

const WhatWeManage = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t, language } = useLanguage();

  const services = [
    {
      title: t('services.service1.title'),
      subtitle: t('services.service1.subtitle'),
      description: serviceDescriptions[language][0],
      image: serviceImages[0],
    },
    {
      title: t('services.service2.title'),
      subtitle: t('services.service2.subtitle'),
      description: serviceDescriptions[language][1],
      image: serviceImages[1],
    },
    {
      title: t('services.service3.title'),
      subtitle: t('services.service3.subtitle'),
      description: serviceDescriptions[language][2],
      image: serviceImages[2],
    },
  ];

  const extras = t('services.extras');

  return (
    <section id="services" ref={ref} className="py-32 bg-gradient-to-b from-[#F8F6F1] to-[#EFECE5] relative overflow-hidden">
      {/* Decorative gold line top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      {/* Decorative corner accents */}
      <div className="absolute top-8 left-8 w-20 h-20 border-l-2 border-t-2 border-[#C5A55C]/30" />
      <div className="absolute top-8 right-8 w-20 h-20 border-r-2 border-t-2 border-[#C5A55C]/30" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="text-center mb-24"
        >
          <motion.span 
            className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {t('services.tagline')}
          </motion.span>
          <h2 className="font-montserrat font-light text-4xl md:text-6xl text-[#1A1A1A] tracking-[0.1em] uppercase">
            {t('services.title')}
          </h2>
          <div className="mt-6 w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>

        {/* Services Grid - Bento Style */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              service={service}
              inView={inView}
              delay={index * 0.2}
              isLarge={index === 0}
            />
          ))}
        </div>

        {/* Additional Services List */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          {extras.map((item, i) => (
            <div key={i} className="group cursor-pointer">
              <span className="font-montserrat font-medium text-sm tracking-[0.2em] text-[#1A1A1A]/50 group-hover:text-[#C5A55C] transition-colors duration-300 uppercase">
                {item}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
      
      {/* Decorative corner accents bottom */}
      <div className="absolute bottom-8 left-8 w-20 h-20 border-l-2 border-b-2 border-[#C5A55C]/30" />
      <div className="absolute bottom-8 right-8 w-20 h-20 border-r-2 border-b-2 border-[#C5A55C]/30" />
    </section>
  );
};

const ServiceCard = ({ service, inView, delay, isLarge }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.8, ease: 'easeOut' }}
      className={`group relative overflow-hidden cursor-pointer bg-white shadow-xl hover:shadow-2xl transition-all duration-500 ${
        isLarge ? 'lg:row-span-2' : ''
      }`}
      data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${isLarge ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
        <motion.img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-all duration-700 ease-out scale-100 group-hover:scale-110"
        />
        
        {/* Light overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Shimmer effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        </div>
        
        {/* Content */}
        <div className="absolute inset-0 p-8 flex flex-col justify-end">
          <span className="font-montserrat font-medium text-[10px] tracking-[0.25em] text-[#C5A55C] uppercase mb-3">
            {service.subtitle}
          </span>
          <h3 className="font-montserrat font-light text-2xl md:text-3xl text-white tracking-wide mb-4">
            {service.title}
          </h3>
          <p className="font-raleway font-light text-sm text-white/90 leading-relaxed tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-500 max-w-sm">
            {service.description}
          </p>
        </div>
        
        {/* Arrow */}
        <div className="absolute top-6 right-6 w-12 h-12 bg-[#C5A55C] flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0">
          <ArrowUpRight className="w-5 h-5 text-white" />
        </div>
      </div>
      
      {/* Gold border on hover */}
      <div className="absolute inset-0 border-4 border-transparent group-hover:border-[#C5A55C] transition-all duration-500 pointer-events-none" />
    </motion.div>
  );
};

export default WhatWeManage;
